/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {Switch, Text} from '@mantine/core';
import React from 'react';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import UserAgentGuidelines from 'llm/chat-library/agent-guidelines/UserAgentGuidelines';
import useUserAgentGuidelines from 'llm/chat-library/agent-guidelines/useUserAgentGuidelines';
import useUserAboutMe from 'llm/chat-library/sidepanel/useUserAboutMe';
import {
  DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY,
  DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED,
} from 'llm/deep-research/DeepResearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredSpinner} from 'ui-library/Spinner';

export const HAS_SEEN_AI_SIDEPANEL_ONBOARDING = 'llm.ai-sidepanel.has-seen-onboarding';

export function AISettingsContent({
  chatId,
  onRestartOnboarding,
}: {
  chatId: string | null;
  onRestartOnboarding?: () => void;
}): JSX.Element {
  const [emailNotificationEnabledPref, setEmailNotificationEnabled] = usePreference<boolean | undefined>(
    DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY
  );
  const emailNotificationEnabled =
    emailNotificationEnabledPref ?? DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED;

  const {guidelines, isLoading, saveGuidelines} = useUserAgentGuidelines();
  const {
    aboutMe,
    isLoading: isAboutMeLoading,
    clearAboutMe,
  } = useUserAboutMe({
    enabled: BenchlingConfig.LLM_ENABLE_AI_ONBOARDING,
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: '280px',
        padding: '4px',
        paddingBottom: '8px',
      }}
    >
      {/* About Me Section */}
      {BenchlingConfig.LLM_ENABLE_AI_ONBOARDING && (
        <>
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h4 style={{margin: 0}}>About you</h4>
              {!isAboutMeLoading && aboutMe && (
                <div style={{display: 'flex', gap: '4px'}}>
                  {onRestartOnboarding && (
                    <Button
                      buttonStyle={ButtonStyle.TEXT}
                      size={ButtonSize.SMALL}
                      text="Redo"
                      onClick={onRestartOnboarding}
                      dataTestAttrs={{component: 'AISettingsContent', element: 'redo-onboarding-button'}}
                    />
                  )}
                  <Button
                    buttonStyle={ButtonStyle.TEXT}
                    size={ButtonSize.SMALL}
                    text="Clear"
                    onClick={clearAboutMe}
                    dataTestAttrs={{component: 'AISettingsContent', element: 'clear-about-me-button'}}
                  />
                </div>
              )}
            </div>
            <Text fw="normal">Personalizes AI responses based on your role.</Text>
            {isAboutMeLoading ? (
              <CenteredSpinner />
            ) : aboutMe ? (
              <div
                style={{
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {aboutMe}
              </div>
            ) : (
              onRestartOnboarding && (
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  text="Start"
                  onClick={onRestartOnboarding}
                  dataTestAttrs={{component: 'AISettingsContent', element: 'start-onboarding-button'}}
                />
              )
            )}
          </div>

          {/* Divider */}
          <div style={{borderTop: '1px solid #e0e0e0', margin: '0 -4px'}} />
        </>
      )}

      {/* Custom Instructions Section */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        <h4>Custom Instructions</h4>
        <Text fw="normal">This will apply to all your conversations.</Text>
        {isLoading || guidelines == null ? (
          <CenteredSpinner />
        ) : (
          <UserAgentGuidelines value={guidelines} onSave={saveGuidelines} />
        )}
      </div>

      {/* Divider */}
      <div style={{borderTop: '1px solid #e0e0e0', margin: '0 -4px'}} />

      {/* Notifications Section */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
        <h4>Notifications</h4>
        <Switch
          checked={emailNotificationEnabled}
          onChange={(event) => {
            setEmailNotificationEnabled(event.currentTarget.checked);
          }}
          labelPosition="left"
          label={<Text fw="normal">Notify me by email when Deep Research reports complete</Text>}
        />
      </div>

      {chatId && (
        <>
          {/* Divider */}
          <div style={{borderTop: '1px solid #e0e0e0', margin: '0 -4px'}} />

          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="audit-trail"
            onClick={() => {
              AuditActions.exportAuditLogs([chatId]);
            }}
            size={ButtonSize.SMALL}
            text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
            data-test-component="AISidepanelV2"
            data-test-element="exportAuditLogButton"
          />
        </>
      )}
    </div>
  );
}
